/**
 * Spotify Service
 *
 * Fetches artist top tracks from Spotify Web API via backend proxy.
 * The backend handles authentication with Client Credentials flow.
 *
 * Features:
 * - Search for artists by name
 * - Get artist's top 4 tracks with preview URLs
 * - Cache results to minimize API calls
 */

import { apiClient } from '@shared/services/apiClient';

export interface SpotifyTrack {
  id: string;
  name: string;
  previewUrl: string | null;
  albumName: string;
  albumImageUrl: string | null;
  durationMs: number;
  artistName: string;
}

export interface SpotifyArtist {
  id: string;
  name: string;
  imageUrl: string | null;
  topTracks: SpotifyTrack[];
}

// In-memory cache for artist data
const artistCache = new Map<string, { data: SpotifyArtist | null; timestamp: number }>();
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Clean artist name for better search results
 * Removes common suffixes and special characters
 */
const cleanArtistName = (name: string): string => {
  return name
    .replace(/ live$/i, '')
    .replace(/ concert$/i, '')
    .replace(/ tour$/i, '')
    .replace(/ world tour$/i, '')
    .replace(/ presents?:?/i, '')
    .replace(/\s*\(.*?\)\s*/g, '') // Remove parenthetical content
    .replace(/\s*-\s*.*$/, '') // Remove everything after dash
    .replace(/\s*[&+]\s*.*$/, '') // Remove featured artists
    .trim();
};

/**
 * Get cache key for artist name
 */
const getCacheKey = (artistName: string): string => {
  return cleanArtistName(artistName).toLowerCase();
};

/**
 * Fetch artist's top tracks from Spotify via backend
 *
 * @param artistName - Name of the artist to search for
 * @returns SpotifyArtist with top tracks or null if not found
 */
export const getArtistTopTracks = async (artistName: string): Promise<SpotifyArtist | null> => {
  const cacheKey = getCacheKey(artistName);

  // Check cache first
  const cached = artistCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  try {
    const cleanedName = cleanArtistName(artistName);
    const response = await apiClient.get<SpotifyArtist>('/spotify/artist-tracks', {
      params: { artist_name: cleanedName, limit: 4 },
    });

    const artist = response.data;

    // Cache the result
    artistCache.set(cacheKey, { data: artist, timestamp: Date.now() });

    return artist;
  } catch (error) {
    console.error('[SpotifyService] Failed to fetch artist tracks:', error);

    // Cache null result to prevent repeated failed requests
    artistCache.set(cacheKey, { data: null, timestamp: Date.now() });

    return null;
  }
};

/**
 * Format duration from milliseconds to MM:SS
 */
export const formatDuration = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Clear the artist cache (for testing/debugging)
 */
export const clearArtistCache = (): void => {
  artistCache.clear();
};
